/**
 * Conversions — Booking funnel, key events, and conversion optimization
 * Data: GA4 Property 484092824 (Feb 26 – Mar 25, 2026)
 */

import React, { useState, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInUp, pageTransition, pageInitial, pageAnimate } from '@/lib/motionVariants';
import { formatNumber, formatPercentRaw } from '@/lib/formatting';
import { BRAND, CHART_COLORS, cssVar } from '../charts/utils';
import { useChatStore } from '@/stores/chatStore';
import ConversionDrillPanel from '../drill/ConversionDrillPanel';
import type { ConversionDrillData } from '../drill/ConversionDrillPanel';
import DeltaIndicator from '../shared/DeltaIndicator';
import { CONVERSION_KPIS, FUNNEL_COMPARISON, CONVERSION_CHANNEL_COMPARISON } from '@/data/comparisonData';
import PageHeader from '../shared/PageHeader';
import { useComparisonPeriods } from '@/hooks/useComparisonPeriods';
import { useFunnel, useChannels } from '@/hooks/useGA4Data';

const askChat = (query: string, e?: React.MouseEvent) => {
  if (e) {
    useChatStore.getState().showAnalyzePrompt(
      query, '', (e.currentTarget as HTMLElement).getBoundingClientRect(),
    );
  } else {
    useChatStore.getState().sendAnalyzeQuery(query);
  }
};

const FUNNEL_STAGES = [
  { label: 'Total Sessions',            value: 87_615, color: BRAND.gold,      pctOfTop: 100 },
  { label: 'Engaged Sessions',          value: 69_649, color: CHART_COLORS[2], pctOfTop: 79.49 },
  { label: 'Reservation Button Clicks', value: 13_000, color: CHART_COLORS[3], pctOfTop: 14.84 },
  { label: 'Form Submissions',          value: 118,    color: CHART_COLORS[5], pctOfTop: 0.13 },
];

const KEY_EVENTS_BY_CHANNEL = [
  { channel: 'Paid Social',    keyEvents: 51, sessions: 29_131, rate: 0.18 },
  { channel: 'Paid Search',    keyEvents: 24, sessions: 11_654, rate: 0.21 },
  { channel: 'Direct',         keyEvents: 17, sessions: 15_236, rate: 0.11 },
  { channel: 'Organic Search', keyEvents: 16, sessions: 22_137, rate: 0.07 },
  { channel: 'Referral',       keyEvents: 5,  sessions: 1_223,  rate: 0.41 },
  { channel: 'Organic Social', keyEvents: 3,  sessions: 4_340,  rate: 0.07 },
  { channel: 'Paid Other',     keyEvents: 2,  sessions: 3_630,  rate: 0.06 },
];

const Conversions: React.FC = () => {
  // Live API data with hardcoded fallbacks
  const { data: liveFunnel } = useFunnel();
  const { data: liveChannels } = useChannels();

  const funnelStages = liveFunnel?.map((f, idx) => ({
    label: f.stage,
    value: f.value,
    color: FUNNEL_STAGES[idx]?.color ?? CHART_COLORS[idx] ?? BRAND.gold,
    pctOfTop: f.pctOfTop,
  })) ?? FUNNEL_STAGES;

  const keyEventsByChannel = liveChannels?.map(ch => ({
    channel: ch.channel,
    keyEvents: ch.keyEvents,
    sessions: ch.sessions,
    rate: ch.sessions > 0 ? ch.keyEvents / ch.sessions * 100 : 0,
  })).filter(ch => ch.keyEvents > 0).sort((a, b) => b.keyEvents - a.keyEvents) ?? KEY_EVENTS_BY_CHANNEL;

  // Conversion drill panel state
  const [drillData, setDrillData] = useState<ConversionDrillData | null>(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const { compareEnabled, popTooltip, yoyTooltip } = useComparisonPeriods();
  const handleFunnelClick = useCallback((stage: typeof FUNNEL_STAGES[number], idx: number) => {
    setDrillData({
      type: 'funnel',
      label: stage.label,
      value: stage.value,
      pctOfTop: stage.pctOfTop,
      prevStageValue: idx > 0 ? funnelStages[idx - 1].value : undefined,
      prevStageLabel: idx > 0 ? funnelStages[idx - 1].label : undefined,
    });
    setDrillOpen(true);
  }, []);
  const handleChannelClick = useCallback((row: typeof KEY_EVENTS_BY_CHANNEL[number]) => {
    setDrillData({
      type: 'channel',
      channel: row.channel,
      keyEvents: row.keyEvents,
      sessions: row.sessions,
      rate: row.rate,
    });
    setDrillOpen(true);
  }, []);
  const handleDrillClose = useCallback(() => {
    setDrillOpen(false);
    setDrillData(null);
  }, []);

  return (
  <motion.div className="space-y-6" initial={pageInitial} animate={pageAnimate} transition={pageTransition}>
    <PageHeader title="Conversions" subtitle="Booking funnel, key event attribution, and conversion optimization" />

    {/* KPIs */}
    <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-3" variants={staggerContainer} initial="hidden" animate="visible">
      <motion.div variants={fadeInUp}>
      <div className="bg-bg-1 border border-monarch-gold/40 rounded-xl p-4">
        <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Reservation Clicks</p>
        <p className="font-mono text-lg font-bold text-monarch-gold">13,000</p>
        {compareEnabled && <div className="mt-1"><DeltaIndicator current={CONVERSION_KPIS.reservationClicks.current} yoyPrior={CONVERSION_KPIS.reservationClicks.yoy} popPrior={CONVERSION_KPIS.reservationClicks.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        <p className="text-xs text-text-3 mt-0.5">nav button clicks</p>
      </div>
      </motion.div>
      <motion.div variants={fadeInUp}>
      <div className="bg-bg-1 border border-danger/30 rounded-xl p-4 cursor-pointer hover:border-danger/60 hover:bg-bg-2 transition-colors" onClick={(e) => askChat('Only 118 form submissions from 87.6K sessions. What is blocking users from completing the form?', e)}>
        <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Form Submissions</p>
        <p className="font-mono text-lg font-bold text-danger">118</p>
        {compareEnabled && <div className="mt-1"><DeltaIndicator current={CONVERSION_KPIS.formSubmissions.current} yoyPrior={CONVERSION_KPIS.formSubmissions.yoy} popPrior={CONVERSION_KPIS.formSubmissions.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        <p className="text-xs text-text-3 mt-0.5">homepage_form_submission</p>
      </div>
      </motion.div>
      <motion.div variants={fadeInUp}>
      <div className="bg-bg-1 border border-danger/30 rounded-xl p-4 cursor-pointer hover:border-danger/60 hover:bg-bg-2 transition-colors" onClick={(e) => askChat('Click-to-submit rate is 0.91% — a 99.1% drop-off. What UX changes could improve this conversion step?', e)}>
        <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Click-to-Submit</p>
        <p className="font-mono text-lg font-bold text-danger">0.91%</p>
        {compareEnabled && <div className="mt-1"><DeltaIndicator current={CONVERSION_KPIS.clickToSubmit.current} yoyPrior={CONVERSION_KPIS.clickToSubmit.yoy} popPrior={CONVERSION_KPIS.clickToSubmit.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        <p className="text-xs text-text-3 mt-0.5">99.1% drop-off</p>
      </div>
      </motion.div>
      <motion.div variants={fadeInUp}>
      <div className="bg-bg-1 border border-border rounded-xl p-4">
        <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Session-to-Key Event</p>
        <p className="font-mono text-lg font-bold text-text">0.13%</p>
        {compareEnabled && <div className="mt-1"><DeltaIndicator current={CONVERSION_KPIS.sessionToKeyEvent.current} yoyPrior={CONVERSION_KPIS.sessionToKeyEvent.yoy} popPrior={CONVERSION_KPIS.sessionToKeyEvent.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        <p className="text-xs text-text-3 mt-0.5">118 of 87,615 sessions</p>
      </div>
      </motion.div>
    </motion.div>

    {/* Funnel Visualization */}
    <div className="bg-bg-1 border border-border rounded-xl p-6">
      <h3 className="font-display text-sm text-text-2 mb-6">Booking Conversion Funnel</h3>
      <div className="space-y-4 max-w-3xl mx-auto">
        {funnelStages.map((stage, idx) => {
          const widthPct = Math.max((stage.value / funnelStages[0].value) * 100, 3);
          const dropOff = idx > 0
            ? ((funnelStages[idx - 1].value - stage.value) / funnelStages[idx - 1].value * 100)
            : null;

          return (
            <div key={stage.label} className="cursor-pointer hover:opacity-80 transition-opacity" onClick={() => handleFunnelClick(stage, idx)}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-text-2">{stage.label}</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs font-mono text-text-3">{stage.pctOfTop}% of total</span>
                  <span className="text-sm font-mono font-bold text-text">{formatNumber(stage.value)}</span>
                  {compareEnabled && (stage.label as string) in FUNNEL_COMPARISON && (
                    <DeltaIndicator current={stage.value} yoyPrior={FUNNEL_COMPARISON[stage.label as keyof typeof FUNNEL_COMPARISON].yoy} popPrior={FUNNEL_COMPARISON[stage.label as keyof typeof FUNNEL_COMPARISON].pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                  )}
                </div>
              </div>
              <div className="relative h-10 bg-bg-2 rounded-lg overflow-hidden">
                <div
                  className="h-full rounded-lg transition-all duration-500"
                  style={{ width: `${widthPct}%`, backgroundColor: stage.color }}
                />
              </div>
              {dropOff != null && dropOff > 0 && (
                <div className="flex items-center justify-end gap-2 mt-1">
                  <span className={`text-xs font-mono ${dropOff > 80 ? 'text-danger font-bold' : 'text-text-3'}`}>
                    -{dropOff.toFixed(1)}% drop-off
                  </span>
                  <span className="text-xs text-text-3">
                    ({formatNumber(funnelStages[idx - 1].value - stage.value)} lost)
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>

    {/* Key Events by Channel */}
    <div className="bg-bg-1 border border-border rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="font-display text-sm text-text-2">Key Events by Channel (Form Submissions)</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-bg-2 text-text-3">
              <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Channel</th>
              <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Key Events</th>
              {compareEnabled && <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Change</th>}
              <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Sessions</th>
              <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Key Event Rate</th>
              <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Performance</th>
              <th className="px-4 py-3 w-8" />
            </tr>
          </thead>
          <tbody>
            {keyEventsByChannel.map((row, idx) => (
              <tr key={idx} className="group border-b border-border/50 hover:bg-bg-2 transition-colors cursor-pointer" onClick={() => handleChannelClick(row)}>
                <td className="px-4 py-3 font-medium text-text">{row.channel}</td>
                <td className="px-4 py-3 text-right font-mono text-text">{row.keyEvents}</td>
                {compareEnabled && (
                  <td className="px-4 py-3 text-right">
                    {CONVERSION_CHANNEL_COMPARISON[row.channel] && (
                      <DeltaIndicator current={row.keyEvents} yoyPrior={CONVERSION_CHANNEL_COMPARISON[row.channel].keyEvents.yoy} popPrior={CONVERSION_CHANNEL_COMPARISON[row.channel].keyEvents.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                    )}
                  </td>
                )}
                <td className="px-4 py-3 text-right font-mono text-text-2">{formatNumber(row.sessions)}</td>
                <td className="px-4 py-3 text-right font-mono text-text">{formatPercentRaw(row.rate, 2)}</td>
                <td className="px-4 py-3">
                  <div className="w-full h-3 bg-bg-2 rounded-full overflow-hidden" style={{ maxWidth: 120 }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min((row.rate / 0.41) * 100, 100)}%`,
                        backgroundColor: row.rate >= 0.2 ? cssVar('--color-success', '#10b981') : row.rate >= 0.1 ? cssVar('--color-warning', '#f59e0b') : cssVar('--color-danger', '#ef4444'),
                      }}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <ChevronRight className="w-4 h-4 text-text-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Insights */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-danger/5 border border-danger/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('13K reservation clicks but only 118 form submissions — 99.1% abandonment. What could be causing this massive drop-off in the booking widget?', e)}>
        <p className="text-sm font-semibold text-text mb-1">Critical: Booking Widget Friction</p>
        <p className="text-xs text-text-3 leading-relaxed">
          13,000 users clicked the reservation button but only 118 completed the form — a 99.1% abandonment rate. This is the single biggest revenue leak. The booking widget (likely a third-party iframe) may be slow to load, have UX issues, or redirect users to an external site. Audit this flow immediately with session recordings.
        </p>
      </div>
      <div className="bg-success/5 border border-success/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('Referral traffic converts at 0.41% — 3x better than any other channel. How do we identify and scale top referring domains?', e)}>
        <p className="text-sm font-semibold text-text mb-1">Referral Converts Best (0.41%)</p>
        <p className="text-xs text-text-3 leading-relaxed">
          Despite only 1,223 sessions, Referral traffic has the highest key event rate at 0.41% — 3x better than any other channel. These are likely travel review sites and partner links sending high-intent visitors. Identify top referring domains and build partnerships to scale this channel.
        </p>
      </div>
    </div>

    {/* Conversion Drill Panel */}
    <ConversionDrillPanel data={drillData} open={drillOpen} onClose={handleDrillClose} />
  </motion.div>
  );
};

export default Conversions;
